// apps/client/src/hooks/useWhoToCall.ts
// Wraps useTrailDropOff to surface a prioritized "Who To Call" list for the dashboard.
// Filters out green alerts (second_year_commencement = opportunities, not calls),
// deduplicates by loan_id (each loan counted once with most severe alert),
// sorts by severity then dollar impact, and limits to top N.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useTrailDropOff, type RunCompletenessInfo } from './useTrailDropOff';

/**
 * A row of the persisted work queue (`listOpenTrailDropOffEvents`).
 *
 * WHY THE QUEUE IS NOT THE REPORT
 * ---------------------------------------------------------------------------
 * `useTrailDropOff` below queries the current and previous payment month only.
 * That is correct for a report (it needs the prior month for LAG context) and
 * wrong for a work queue: an alert raised in July that nobody actioned simply
 * drops out of the dashboard in September while still being open. Filtering the
 * two-month window cannot fix that, because the older rows are not in the window
 * to filter. Persisted events have no window, so the queue uses those.
 */
interface OpenDropOffEventRow {
  eventId: string;
  clientId: string;
  loanId: string;
  periodMonth: string;
  customerName: string | null;
  lender: string | null;
  amountCents: number | null;
  trailCents: number | null;
  priorMonthCents: number | null;
  dropOffAlertType: AlertType | null;
  brokerDisplay: string | null;
  officeId: string | null;
}

type AlertType = 'current_month_dropoff' | 'previous_months_dropoff' | 'historical_dropoff' | 'significant_variance';

export interface WhoToCallRecord {
  loanId: string;
  customerName: string | null;
  brokerDisplay: string;
  lender: string | null;
  alertType: AlertType;
  /** Monthly trail lost in cents (positive = loss) */
  dollarImpactCents: number;
  variancePercent: number | null;
  priorMonthCents: number | null;
  trailCents: number;
  paymentMonth: string;
  officeId: string | null;
}

export interface UseWhoToCallReturn {
  records: WhoToCallRecord[];
  totalAlerts: number;
  /** Sum of monthly losses * 12 (annualized, in cents) */
  totalAnnualRiskCents: number;
  urgentCount: number;
  churnedCount: number;
  /** Unresolved drop-offs that started before the widget's window. Report-page work. */
  earlierUnresolvedCount: number;
  warningCount: number;
  loading: boolean;
  error: string | null;
  /** True if any sequential template is missing runs for the end month */
  hasIncompleteData: boolean;
  /** Human-readable warning, e.g. "Run 3 pending — alerts reflect Runs 1-2 only" */
  incompleteDataWarning: string | null;
}

// 'recovered_dropoff' is deliberately absent: the trail resumed, so it is not a
// call. It is filtered out of `actionable` below and therefore out of the
// annualised risk figure too.
const ALERT_PRIORITY: Record<AlertType, number> = {
  current_month_dropoff: 4,
  previous_months_dropoff: 3,
  historical_dropoff: 2,
  significant_variance: 1,
};

/**
 * Hook for the dashboard "Who To Call" accordion widget.
 * Reuses the existing getTrailDropOffReport backend — no new Cloud Function needed.
 *
 * Key design decisions:
 * - Queries last 1 month (prev + current) to get recent alerts
 * - Deduplicates by loan_id: each loan counted ONCE with its most severe alert
 *   (backend returns per-(loan, month) rows which inflates counts)
 * - Counts reflect unique loans needing attention, not total alert instances
 */
export function useWhoToCall({
  companyId,
  officeId,
  limit = 5,
}: {
  companyId: string;
  officeId?: string;
  limit?: number;
}): UseWhoToCallReturn {
  // Query previous month + current month (2-month window for LAG context)
  // Narrower than 3-month window to reduce multi-counting before dedup.
  //
  // These two months are derived from the WALL CLOCK, and commission data lands a
  // month or more in arrears, so on a lagging tenant the window runs entirely past
  // the newest data and the strip goes silently empty (My Mortgage Freedom, newest
  // period 2026-07, would show nothing from 1 September 2026). `anchorWindowToLatestData`
  // makes the backend slide this window back to the tenant's newest COMPLETED
  // upload month, keeping the two-month span. It is a no-op for a tenant whose
  // data is current, which is every non-lagging tenant.
  const now = new Date();
  const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const startMonth = `${oneMonthAgo.getFullYear()}-${String(oneMonthAgo.getMonth() + 1).padStart(2, '0')}`;
  const endMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const {
    records: rawRecords,
    loading,
    error,
    runCompleteness,
  } = useTrailDropOff({
    companyId,
    startMonth,
    endMonth,
    officeId,
    alertType: 'all',
    anchorWindowToLatestData: true,
  });

  // Compute run completeness warning for sequential templates missing runs
  const { hasIncompleteData, incompleteDataWarning } = useMemo(() => {
    const incomplete = runCompleteness.filter(
      (rc) => rc.runStrategy === 'sequential' && !rc.isComplete
    );
    if (incomplete.length === 0) {
      return { hasIncompleteData: false, incompleteDataWarning: null };
    }
    // Find the highest missing run across all incomplete templates
    const allMissing = new Set<number>();
    for (const rc of incomplete) {
      const available = new Set(rc.availableRuns);
      for (let r = 1; r <= rc.expectedRunCount; r++) {
        if (!available.has(r)) allMissing.add(r);
      }
    }
    const missingRuns = Array.from(allMissing).sort((a, b) => a - b);
    const runLabel = missingRuns.length === 1
      ? `Run ${missingRuns[0]}`
      : `Runs ${missingRuns.join(' & ')}`;
    // All incomplete templates have the same available runs — show which runs alerts reflect
    const maxAvailable = Math.max(...incomplete.flatMap((rc) => rc.availableRuns), 0);
    const availableLabel = maxAvailable > 0
      ? ` — alerts reflect Run${maxAvailable > 1 ? `s 1-${maxAvailable}` : ' 1'} only`
      : '';
    return {
      hasIncompleteData: true,
      incompleteDataWarning: `${runLabel} pending${availableLabel}`,
    };
  }, [runCompleteness]);

  // --- persisted work queue (preferred source once the tenant is enabled) -----
  const [queue, setQueue] = useState<{
    enabled: boolean;
    rows: OpenDropOffEventRow[];
    total: number;
    truncated: boolean;
  } | null>(null);
  const [queueError, setQueueError] = useState<string | null>(null);

  const loadQueue = useCallback(async () => {
    if (!companyId) return;
    try {
      const fn = httpsCallable(functions, 'listOpenTrailDropOffEvents');
      const res = await fn({ companyId, officeId, limit: 200 });
      setQueue(res.data as typeof queue);
      setQueueError(null);
    } catch (err) {
      // Rule-gated read: never silent-fail. Falling back to the report path is a
      // degraded but honest answer; pretending the queue is empty would render
      // "nobody to call", which is the one wrong thing this widget can say.
      console.error('[useWhoToCall] listOpenTrailDropOffEvents failed', err);
      setQueueError(err instanceof Error ? err.message : 'Failed to load the call queue.');
      setQueue(null);
    }
  }, [companyId, officeId]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const result = useMemo(() => {
    // Preferred path: the tenant tracks completion, so the queue already contains
    // only OPEN work, at every age, with completed and dismissed occurrences
    // excluded server-side. Nothing here can age out and nothing completed can
    // reappear.
    if (queue?.enabled) {
      const byLoanOpen = new Map<string, OpenDropOffEventRow>();
      for (const r of queue.rows) {
        // Dedupe by loan AFTER the server has already dropped closed work, which
        // is the ordering that matters: deduping first would let a completed
        // occurrence win on severity and then be filtered, hiding a loan whose
        // other month is still open.
        const existing = byLoanOpen.get(r.loanId);
        const impact = Math.max(0, (r.priorMonthCents ?? 0) - (r.trailCents ?? 0));
        if (!existing) {
          byLoanOpen.set(r.loanId, r);
          continue;
        }
        const existingPriority = ALERT_PRIORITY[existing.dropOffAlertType as AlertType] || 0;
        const newPriority = ALERT_PRIORITY[r.dropOffAlertType as AlertType] || 0;
        const existingImpact = Math.max(0, (existing.priorMonthCents ?? 0) - (existing.trailCents ?? 0));
        if (newPriority > existingPriority || (newPriority === existingPriority && impact > existingImpact)) {
          byLoanOpen.set(r.loanId, r);
        }
      }

      const mappedQueue: WhoToCallRecord[] = Array.from(byLoanOpen.values())
        .map((r) => {
          const prior = r.priorMonthCents ?? 0;
          const trail = r.trailCents ?? 0;
          return {
            loanId: r.loanId,
            customerName: r.customerName,
            brokerDisplay: r.brokerDisplay ?? '',
            lender: r.lender,
            alertType: (r.dropOffAlertType ?? 'current_month_dropoff') as AlertType,
            dollarImpactCents: Math.max(0, prior - trail),
            variancePercent: prior > 0 ? Math.round(((prior - trail) / prior) * 1000) / 10 : null,
            priorMonthCents: r.priorMonthCents,
            trailCents: trail,
            paymentMonth: r.periodMonth,
            officeId: r.officeId,
          };
        })
        .sort((a, b) => {
          const ap = ALERT_PRIORITY[a.alertType] || 0;
          const bp = ALERT_PRIORITY[b.alertType] || 0;
          if (ap !== bp) return bp - ap;
          return b.dollarImpactCents - a.dollarImpactCents;
        });

      return {
        records: mappedQueue.slice(0, limit),
        // Counters computed POST-filter, so the headline describes open work
        // rather than raw detections.
        totalAlerts: mappedQueue.length,
        totalAnnualRiskCents: mappedQueue.reduce((s, r) => s + r.dollarImpactCents, 0) * 12,
        urgentCount: mappedQueue.filter((r) => r.alertType === 'current_month_dropoff').length,
        churnedCount: mappedQueue.filter((r) => r.alertType === 'previous_months_dropoff').length,
        earlierUnresolvedCount: mappedQueue.filter((r) => r.alertType === 'historical_dropoff').length,
        warningCount: mappedQueue.filter((r) => r.alertType === 'significant_variance').length,
      };
    }

    // Fallback while the tenant has no completion tracking yet. Unchanged
    // behaviour on purpose: retention tracking ships dark, so on deploy day every
    // tenant is here, and the widget must look exactly as it did.
    // Filter out green alerts (second_year_commencement) and null alerts
    const actionable = rawRecords.filter(
      (r) =>
        r.alertType === 'current_month_dropoff' ||
        r.alertType === 'previous_months_dropoff' ||
        r.alertType === 'historical_dropoff' ||
        r.alertType === 'significant_variance'
    );

    // Deduplicate by loan_id — keep the most severe alert for each loan.
    // Without this, the same loan appearing in multiple months inflates the count
    // (e.g., 843 alerts when only ~300 unique loans need attention).
    const byLoan = new Map<string, typeof actionable[0]>();
    for (const r of actionable) {
      const existing = byLoan.get(r.loanId);
      if (!existing) {
        byLoan.set(r.loanId, r);
      } else {
        const existingPriority = ALERT_PRIORITY[existing.alertType as AlertType] || 0;
        const newPriority = ALERT_PRIORITY[r.alertType as AlertType] || 0;
        if (newPriority > existingPriority) {
          byLoan.set(r.loanId, r);
        } else if (newPriority === existingPriority) {
          // Same severity: keep higher dollar impact
          const existingImpact = Math.max(0, (existing.priorMonthCents || 0) - existing.trailCents);
          const newImpact = Math.max(0, (r.priorMonthCents || 0) - r.trailCents);
          if (newImpact > existingImpact) {
            byLoan.set(r.loanId, r);
          }
        }
      }
    }

    const unique = Array.from(byLoan.values());

    // Sort: severity DESC, then dollar impact DESC
    unique.sort((a, b) => {
      const aPriority = ALERT_PRIORITY[a.alertType as AlertType] || 0;
      const bPriority = ALERT_PRIORITY[b.alertType as AlertType] || 0;
      if (aPriority !== bPriority) return bPriority - aPriority;

      // Within same priority: sort by dollar impact (largest loss first)
      const aImpact = Math.max(0, (a.priorMonthCents || 0) - a.trailCents);
      const bImpact = Math.max(0, (b.priorMonthCents || 0) - b.trailCents);
      return bImpact - aImpact;
    });

    // Map to WhoToCallRecord with computed dollar impact
    const mapped: WhoToCallRecord[] = unique.map((r) => ({
      loanId: r.loanId,
      customerName: r.customerName,
      brokerDisplay: r.brokerDisplay,
      lender: r.lender,
      alertType: r.alertType as AlertType,
      dollarImpactCents: Math.max(0, (r.priorMonthCents || 0) - r.trailCents),
      variancePercent: r.variancePercent,
      priorMonthCents: r.priorMonthCents,
      trailCents: r.trailCents,
      paymentMonth: r.paymentMonth,
      officeId: r.officeId,
    }));

    // Annualized risk = sum of unique loan monthly losses * 12
    const totalAnnualRiskCents = mapped.reduce((sum, r) => sum + r.dollarImpactCents, 0) * 12;

    // Count unique loans per alert type (not per-month instances)
    const urgentCount = mapped.filter((r) => r.alertType === 'current_month_dropoff').length;
    const churnedCount = mapped.filter((r) => r.alertType === 'previous_months_dropoff').length;
    const earlierUnresolvedCount = mapped.filter((r) => r.alertType === 'historical_dropoff').length;
    const warningCount = mapped.filter((r) => r.alertType === 'significant_variance').length;

    return {
      records: mapped.slice(0, limit),
      totalAlerts: mapped.length,
      totalAnnualRiskCents,
      urgentCount,
      churnedCount,
      earlierUnresolvedCount,
      warningCount,
    };
  }, [rawRecords, limit, queue]);

  return {
    ...result,
    loading,
    // Surface a queue failure rather than swallowing it: the widget must be able
    // to say "status unavailable" instead of implying an empty call list.
    error: error ?? queueError,
    hasIncompleteData,
    incompleteDataWarning,
  };
}
